import {useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent} from 'react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {Badge} from '@/components/ui/badge'
import {AlertTriangle} from 'lucide-react'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {MutedText} from '@/components/shared/MutedText'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {SqlEditorPanel} from '@/components/editors/SqlEditorPanel'
import {ResultTable} from '@/pages/reporting/ResultTable'
import {useSqlPreview} from '@/hooks/use-sql-preview'
import {
    SqlPreviewBinding,
    SqlValueType as SqlValueTypeWire,
    SqlPreviewErrorCode,
    SqlPreviewLimit,
    type SqlPreviewRequest,
    type SqlPreviewResponse,
} from '@/types/sql-preview'
import type {SqlExecute} from '@/pages/reporting/types'

// WHY (1122): the wire values live in @/types/sql-preview (SqlPreviewBinding);
// this UPPER_CASE-keyed re-projection is what the dialog API exposes to callers
// and tests. Sourcing values from SqlPreviewBinding keeps the wire literals out
// of this file.
export const SqlBindingMethod = {
    QUERY: SqlPreviewBinding.query,
    QUERY_FOR_VALUE: SqlPreviewBinding.queryForValue,
    UPDATE: SqlPreviewBinding.update,
    EXECUTE: SqlPreviewBinding.execute,
} as const
export type SqlBindingMethod = typeof SqlBindingMethod[keyof typeof SqlBindingMethod]

const SQL_BINDING_METHOD_VALUES = new Set<string>(Object.values(SqlBindingMethod))
function isSqlBindingMethod(v: string): v is SqlBindingMethod {
    return SQL_BINDING_METHOD_VALUES.has(v)
}

export const SqlValueType = SqlValueTypeWire
export type SqlValueType = typeof SqlValueType[keyof typeof SqlValueType]

const SQL_VALUE_TYPE_VALUES = new Set<string>(Object.values(SqlValueType))
function isSqlValueType(v: string): v is SqlValueType {
    return SQL_VALUE_TYPE_VALUES.has(v)
}

const SQL_VALUE_TYPE_ORDER: ReadonlyArray<SqlValueType> = [
    SqlValueType.Long,
    SqlValueType.String,
    SqlValueType.Integer,
    SqlValueType.BigDecimal,
    SqlValueType.Boolean,
    SqlValueType.Instant,
    SqlValueType.UUID,
]

const LOADING_FLASH_PREVENTION_MS = 300

export interface DialogState {
    sql: string
    binding: SqlBindingMethod
    paramsLiteral: string
    valueType: SqlValueType
}

export interface ScriptSqlDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    initialSql?: string
    hasSelection: boolean
    onInsert: (groovyCode: string) => void
    /**
     * Set by the parent (529.04 wires it from the actual preview response).
     * When true, render the variable-ref hint below the Parameters textarea.
     */
    previewParamsRejected?: boolean
}

export function ScriptSqlDialog({
    open,
    onOpenChange,
    initialSql,
    hasSelection,
    onInsert,
    previewParamsRejected = false,
}: ScriptSqlDialogProps) {
    const i18n = useI18n()
    const [state, setState] = useState<DialogState>({
        sql: initialSql ?? '',
        binding: SqlBindingMethod.QUERY,
        paramsLiteral: '',
        valueType: SqlValueType.Long,
    })
    const {isRunning, result: previewResponse, error, run: runPreview, reset} = useSqlPreview()
    const result = useMemo(() => adaptPreviewResponse(previewResponse), [previewResponse])
    const [paramsRejectedFromMutation, setParamsRejectedFromMutation] = useState(false)
    const effectivePreviewParamsRejected = previewParamsRejected || paramsRejectedFromMutation
    const sqlEditorWrapperRef = useRef<HTMLDivElement>(null)
    const paramsTextareaRef = useRef<HTMLTextAreaElement>(null)
    const resultTableWrapperRef = useRef<HTMLDivElement>(null)
    const errorBannerWrapperRef = useRef<HTMLDivElement>(null)
    const [showSkeleton, setShowSkeleton] = useState(false)
    const inFlightRef = useRef(false)
    const lastResultRef = useRef<typeof result>(null)
    const lastErrorRef = useRef<Error | null>(null)

    useEffect(() => {
        if (open) {
            setState({
                sql: initialSql ?? '',
                binding: SqlBindingMethod.QUERY,
                paramsLiteral: '',
                valueType: SqlValueType.Long,
            })
            reset()
            inFlightRef.current = false
            setShowSkeleton(false)
            setParamsLiteralDirty(false)
            setParamsRejectedFromMutation(false)
            lastResultRef.current = null
            lastErrorRef.current = null
        }
    }, [open, initialSql, reset])

    // WHY: dirty tracks "edits since the most recent rejection event", not
    // "edits since dialog open". When the parent flips previewParamsRejected
    // to true, we MUST reset dirty — otherwise the typical edit→Try-SQL→reject
    // flow leaves dirty=true at rejection time, so `previewParamsRejected &&
    // !dirty` evaluates to false and the hint never displays. The dialog-open
    // reset above handles the dialog-open case; this effect handles the
    // rejection-after-edit case. (858 §3 follow-up.)
    useEffect(() => {
        if (effectivePreviewParamsRejected) {
            setParamsLiteralDirty(false)
        }
    }, [effectivePreviewParamsRejected])

    useEffect(() => {
        if (!isRunning) {
            setShowSkeleton(false)
            return
        }
        const handle = window.setTimeout(() => setShowSkeleton(true), LOADING_FLASH_PREVENTION_MS)
        return () => window.clearTimeout(handle)
    }, [isRunning])

    useEffect(() => {
        if (!open) {
            return
        }
        if (result && result !== lastResultRef.current) {
            lastResultRef.current = result
            const t = window.setTimeout(() => {
                resultTableWrapperRef.current?.focus()
            }, 0)
            return () => window.clearTimeout(t)
        }
        if (error && error !== lastErrorRef.current) {
            lastErrorRef.current = error
            const t = window.setTimeout(() => {
                errorBannerWrapperRef.current?.focus()
            }, 0)
            return () => window.clearTimeout(t)
        }
    }, [open, result, error])

    const [paramsLiteralDirty, setParamsLiteralDirty] = useState(false)

    const groovyCode = useMemo(() => generateGroovyCode(state), [state])
    const sqlEmpty = !state.sql.trim()
    const tryDisabled = sqlEmpty || isRunning
    const insertDisabled = sqlEmpty
    const insertLabel = hasSelection
        ? i18n.scriptSqlV2.actions.replace
        : i18n.scriptSqlV2.actions.insert
    const showResultType = state.binding === SqlBindingMethod.QUERY_FOR_VALUE
    const showExecuteCaption = state.binding === SqlBindingMethod.EXECUTE
    const showExecuteParamsIgnored =
        state.binding === SqlBindingMethod.EXECUTE && state.paramsLiteral.trim() !== ''
    // WHY: keep variable-ref hint until the user actually edits the textarea —
    // otherwise the user fixes the input and the hint plus aria-invalid linger
    // until the next preview round-trip (which may never come).
    const showVariableRefHint = effectivePreviewParamsRejected && !paramsLiteralDirty
    // WHY (1122): the only i18n surface this memo touches is the errorBanners
    // sub-tree; widening the dep to the full `i18n` proxy would re-run the
    // memo on every unrelated translation access in the tree.
    const errorBannersCopy = i18n.scriptSqlV2.errorBanners
    const bannerVariant = useMemo(() => {
        const descriptor = deriveBannerDescriptor(error, state.binding, state.valueType)
        return descriptor ? resolveBannerCopy(descriptor, errorBannersCopy) : null
    }, [error, state.binding, state.valueType, errorBannersCopy])

    const handleRun = useCallback(() => {
        if (tryDisabled || inFlightRef.current) {
            return
        }
        inFlightRef.current = true
        // WHY (529.04 HQG §11): pre-reset paramsRejectedFromMutation before each
        // submission so two consecutive identical rejections still produce a
        // false→true transition. Without this, .catch sets the same true value
        // twice, React bails the re-render, the dirty-reset effect doesn't fire,
        // and the variable-ref hint stays hidden after the second rejection.
        setParamsRejectedFromMutation(false)
        // WHY (529.04 HQG §3): pass all fields through; useSqlPreview.buildWireBody owns
        // wire-body filtering (drops empty paramsLiteral, omits valueType outside
        // queryForValue). Keeping the filter at the wire boundary keeps debugging
        // single-source ("why isn't valueType sent" → check buildWireBody).
        const req: SqlPreviewRequest = {
            sql: state.sql,
            binding: state.binding,
            paramsLiteral: state.paramsLiteral,
            valueType: state.valueType,
        }
        runPreview(req)
            .then(() => setParamsRejectedFromMutation(false))
            // NOTE: drives textarea-highlight only; banner reads useSqlPreview().error.
            .catch((e) => {
                const code = (e as {errorCode?: string})?.errorCode
                setParamsRejectedFromMutation(code === SqlPreviewErrorCode.paramsInvalid)
            })
            .finally(() => {
                inFlightRef.current = false
            })
    }, [runPreview, state.sql, state.binding, state.paramsLiteral, state.valueType, tryDisabled])

    function handleInsert() {
        onInsert(groovyCode)
        onOpenChange(false)
    }

    function handleDialogKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            handleRun()
        }
    }

    const handleDialogEscapeKeyDown = useCallback((e: globalThis.KeyboardEvent) => {
        const editorEl = sqlEditorWrapperRef.current
        if (!editorEl) {
            return
        }
        const target = e.target as Node | null
        if (target && editorEl.contains(target)) {
            e.preventDefault()
            paramsTextareaRef.current?.focus()
        }
    }, [])

    useEffect(() => {
        if (!open) {
            return
        }
        const editorEl = sqlEditorWrapperRef.current
        if (!editorEl) {
            return
        }
        const handler = (e: globalThis.KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'm') {
                e.preventDefault()
                paramsTextareaRef.current?.focus()
            }
        }
        editorEl.addEventListener('keydown', handler)
        return () => editorEl.removeEventListener('keydown', handler)
    }, [open])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-4xl"
                data-testid="sql-dialog"
                data-help-prefix="scriptSqlDialog"
                aria-labelledby="sql-dialog-title"
                onKeyDown={handleDialogKeyDown}
                onEscapeKeyDown={handleDialogEscapeKeyDown}
            >
                <DialogHeader>
                    <DialogTitle id="sql-dialog-title">{i18n.scripts.sqlInsertTitle}</DialogTitle>
                    <DialogDescription>{i18n.scripts.sqlInsertDescription}</DialogDescription>
                </DialogHeader>

                <div
                    ref={sqlEditorWrapperRef}
                    data-testid="sql-dialog-sql-editor"
                >
                    <SqlEditorPanel
                        sql={state.sql}
                        onChange={(sql) => setState((s) => ({...s, sql}))}
                        onRun={handleRun}
                        isRunning={isRunning}
                        minHeight="180px"
                        maxHeight="280px"
                        readOnly={isRunning}
                        showRunButton={false}
                    />
                    <div className="text-right">
                        <MutedText size="xs">
                            {i18n.scriptSqlV2.captions.codeMirrorEscapeHint}
                        </MutedText>
                    </div>
                </div>

                <div data-help-key="inlineLiteralAdvisory">
                    <MutedText
                        size="sm"
                        data-testid="sql-dialog-inline-literal-advisory"
                    >
                        {i18n.scriptSqlV2.captions.inlineLiteralAdvisory}
                    </MutedText>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="sql-dialog-params">
                        {i18n.scriptSqlV2.fields.parameters}
                    </Label>
                    <div data-help-key="parameters">
                        <Textarea
                            id="sql-dialog-params"
                            ref={paramsTextareaRef}
                            data-testid="sql-dialog-params-textarea"
                            value={state.paramsLiteral}
                            onChange={(e) => {
                                setState((s) => ({...s, paramsLiteral: e.target.value}))
                                setParamsLiteralDirty(true)
                            }}
                            placeholder={i18n.scriptSqlV2.placeholders.parameters}
                            rows={2}
                            readOnly={isRunning}
                            aria-invalid={showVariableRefHint}
                            aria-describedby={showVariableRefHint ? 'sql-dialog-params-error' : undefined}
                        />
                    </div>
                    {showVariableRefHint && (
                        <div id="sql-dialog-params-error" data-help-key="variableRefHint">
                            <MutedText
                                size="xs"
                                data-testid="sql-dialog-variable-ref-hint"
                            >
                                {i18n.scriptSqlV2.captions.variableRefHint}
                            </MutedText>
                        </div>
                    )}
                    {showExecuteParamsIgnored && (
                        <div data-help-key="executeParamsIgnored">
                            <MutedText
                                size="xs"
                                data-testid="sql-dialog-execute-params-ignored"
                            >
                                {i18n.scriptSqlV2.captions.executeParamsIgnored}
                            </MutedText>
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="select-binding-method">
                        {i18n.scripts.sqlBinding}
                    </Label>
                    <Select
                        value={state.binding}
                        onValueChange={(v) => {
                            if (!isSqlBindingMethod(v)) return
                            setState((s) => ({...s, binding: v}))
                        }}
                        disabled={isRunning}
                    >
                        <SelectTrigger
                            id="select-binding-method"
                            data-testid="sql-dialog-binding-selector"
                            aria-label={i18n.scripts.sqlBinding}
                            aria-describedby={showExecuteCaption ? 'sql-dialog-execute-caption' : undefined}
                        >
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={SqlBindingMethod.QUERY}>{i18n.scripts.sqlBindingOptions.query}</SelectItem>
                            <SelectItem value={SqlBindingMethod.QUERY_FOR_VALUE}>{i18n.scripts.sqlBindingOptions.queryForValue}</SelectItem>
                            <SelectItem value={SqlBindingMethod.UPDATE}>{i18n.scripts.sqlBindingOptions.update}</SelectItem>
                            <SelectItem value={SqlBindingMethod.EXECUTE}>{i18n.scripts.sqlBindingOptions.execute}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {showResultType && (
                    <div className="space-y-2">
                        <Label htmlFor="select-result-type">
                            {i18n.scriptSqlV2.fields.resultType}
                        </Label>
                        <div data-help-key="resultType">
                            <Select
                                value={state.valueType}
                                onValueChange={(v) => {
                                    if (!isSqlValueType(v)) return
                                    setState((s) => ({...s, valueType: v}))
                                }}
                                disabled={isRunning}
                            >
                                <SelectTrigger
                                    id="select-result-type"
                                    data-testid="sql-dialog-result-type-selector"
                                    aria-label={i18n.scriptSqlV2.fields.resultType}
                                >
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {SQL_VALUE_TYPE_ORDER.map((t) => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {showExecuteCaption && (
                    <div id="sql-dialog-execute-caption" data-help-key="executeCaption">
                        <MutedText
                            size="xs"
                            data-testid="sql-dialog-execute-caption"
                        >
                            {i18n.scriptSqlV2.captions.executeRestriction}
                        </MutedText>
                    </div>
                )}

                <div className="flex items-center justify-end">
                    <Button
                        type="button"
                        size="sm"
                        onClick={handleRun}
                        disabled={tryDisabled}
                        aria-disabled={tryDisabled || undefined}
                        data-testid="sql-dialog-try-sql-btn"
                    >
                        {isRunning
                            ? <><ButtonSpinner/>{i18n.scriptSqlV2.actions.running}</>
                            : i18n.scriptSqlV2.actions.trySql}
                    </Button>
                </div>

                {bannerVariant && (
                    <div
                        ref={errorBannerWrapperRef}
                        tabIndex={-1}
                        data-testid="sql-dialog-error-banner"
                    >
                        <InfoBanner variant="destructive" icon={AlertTriangle}>
                            <strong>{bannerVariant.heading}</strong>
                            <p>{bannerVariant.body}</p>
                        </InfoBanner>
                    </div>
                )}

                {showSkeleton && isRunning && (
                    <Skeleton data-testid="sql-dialog-loading-skeleton" className="h-24 w-full"/>
                )}

                {result && !isRunning && (
                    <div ref={resultTableWrapperRef} tabIndex={-1} data-testid="sql-dialog-result-table">
                        <div className="flex items-center gap-2 mb-2">
                            <div data-help-key="rollbackBadge">
                                <Badge
                                    variant="secondary"
                                    data-testid="sql-dialog-rollback-badge"
                                    aria-label={i18n.scriptSqlV2.aria.rollbackBadge}
                                >
                                    {i18n.scriptSqlV2.badges.rolledBack}
                                </Badge>
                            </div>
                        </div>
                        <ResultTable
                            result={result}
                            queryId=""
                            isRunning={isRunning}
                        />
                    </div>
                )}

                <div className="space-y-2">
                    <p className="text-sm font-medium">{i18n.scripts.sqlPreview}</p>
                    <pre
                        className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap"
                        data-testid="sql-dialog-groovy-preview"
                        data-help-key="groovyPreview"
                        aria-live="polite"
                    >
                        {sqlEmpty ? i18n.scriptSqlV2.placeholders.groovyPreview : groovyCode}
                    </pre>
                </div>

                <div data-help-key="blocklistCaption">
                    <MutedText
                        size="xs"
                        data-testid="sql-dialog-blocklist-caption"
                    >
                        {i18n.scriptSqlV2.captions.blocklist}
                    </MutedText>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="sql-dialog-cancel-btn"
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleInsert}
                        disabled={insertDisabled}
                        data-testid="sql-dialog-insert-btn"
                    >
                        {insertLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

const TRIPLE_DOUBLE_QUOTE = '"""'
const TRIPLE_SINGLE_QUOTE = "'''"

// WHY (1122): tag function for Groovy code generation — the i18next/no-literal-string
// rule's `callees` allowlist treats template strings passed to this tag as
// non-translatable source code, so each call site no longer needs an inline
// eslint-disable. Behaviour identical to an untagged template literal.
function groovyTemplate(strings: TemplateStringsArray, ...values: unknown[]): string {
    return String.raw({raw: strings}, ...values)
}

export function generateGroovyCode(state: DialogState): string {
    return buildGroovyCode(state.sql, state.binding, state.paramsLiteral, state.valueType)
}

export function buildGroovyCode(
    rawSql: string,
    binding: SqlBindingMethod,
    paramsLiteral: string = '',
    valueType: SqlValueType = SqlValueType.Long,
): string {
    const cleaned = rawSql.replace(/[\s;]+$/, '')
    const literal = quoteGroovyMultiLine(cleaned)
    const params = paramsLiteral.trim()
    switch (binding) {
        case SqlBindingMethod.QUERY_FOR_VALUE:
            return params
                ? groovyTemplate`def value = sql.queryForValue(${literal}, ${valueType}.class, ${paramsLiteral})`
                : groovyTemplate`def value = sql.queryForValue(${literal}, ${valueType}.class)`
        case SqlBindingMethod.UPDATE:
            return params
                ? groovyTemplate`def affected = sql.update(${literal}, ${paramsLiteral})`
                : groovyTemplate`def affected = sql.update(${literal})`
        case SqlBindingMethod.EXECUTE:
            // WHY: execute() takes no params bag — the textarea is intentionally
            // ignored here (the dialog surfaces a caption so the user knows).
            return groovyTemplate`sql.execute(${literal})`
        case SqlBindingMethod.QUERY:
            return params
                ? groovyTemplate`def rows = sql.query(${literal}, ${paramsLiteral})`
                : groovyTemplate`def rows = sql.query(${literal})`
    }
    // WHY: exhaustiveness check — adding a new SqlBindingMethod value will
    // fail to compile here until the switch is updated.
    const _exhaustive: never = binding
    return _exhaustive
}

function quoteGroovyMultiLine(s: string): string {
    if (!s.includes(TRIPLE_DOUBLE_QUOTE)) {
        return `${TRIPLE_DOUBLE_QUOTE}${s}${TRIPLE_DOUBLE_QUOTE}`
    }
    if (!s.includes(TRIPLE_SINGLE_QUOTE)) {
        return `${TRIPLE_SINGLE_QUOTE}${s}${TRIPLE_SINGLE_QUOTE}`
    }
    // WHY: SQL contains both """ and ''' — fall back to single-quoted
    // single-line form with newline-escape so the inserted snippet is at
    // least syntactically valid Groovy. Pathological case; rare.
    const escaped = s
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        // eslint-disable-next-line i18next/no-literal-string -- Groovy char-escape
        .replace(/\n/g, '\\n')
        // eslint-disable-next-line i18next/no-literal-string -- Groovy char-escape
        .replace(/\r/g, '\\r')
    return `'${escaped}'`
}

export interface BannerVariant {
    heading: string
    body: string
}

/**
 * Pure decision result of {@link deriveBannerDescriptor}: which error-banner
 * variant applies plus any interpolation values. Copy resolution (heading /
 * body text) happens separately in {@link resolveBannerCopy} so the decision
 * logic stays i18n-independent and unit-testable without a render context.
 */
export type BannerDescriptor =
    | {kind: 'networkFailure'}
    | {kind: 'permissionDenied'}
    | {kind: 'serverError'}
    | {kind: 'rateLimit'; retryAfterSeconds: number}
    | {kind: 'limitRowsPerQuery'}
    | {kind: 'limitStatementTimeout'}
    | {kind: 'limitResponsePayload'}
    | {kind: 'limitGeneric'}
    | {kind: 'paramsInvalid'}
    | {kind: 'typeConversionError'; selectedType: SqlValueType}
    // message present → backend-supplied SQL error text; absent → no diagnostic
    | {kind: 'sqlError'; message?: string}

interface BannerSource {
    errorCode?: string
    limit?: string
    retryAfterSeconds?: number
    httpStatus?: number
    message?: string
    isNetworkFailure?: boolean
}

const RATE_LIMIT_DEFAULT_RETRY_SECONDS = 60

export function deriveBannerDescriptor(
    err: unknown,
    binding: SqlBindingMethod,
    valueType: SqlValueType,
): BannerDescriptor | null {
    if (!err) {
        return null
    }
    const source = extractBannerSource(err)
    if (source.isNetworkFailure) {
        return {kind: 'networkFailure'}
    }
    if (source.httpStatus === 403) {
        return {kind: 'permissionDenied'}
    }
    if (source.httpStatus !== undefined && source.httpStatus >= 500) {
        return {kind: 'serverError'}
    }
    if (source.errorCode === SqlPreviewErrorCode.rateLimitExceeded) {
        // WHY: a missing retryAfterSeconds shouldn't render "Try again in 0
        // seconds" (reads as "try immediately", contradicts rate-limit
        // semantics). Default to 60 — same order of magnitude as the typical
        // backend window.
        return {kind: 'rateLimit', retryAfterSeconds: source.retryAfterSeconds ?? RATE_LIMIT_DEFAULT_RETRY_SECONDS}
    }
    if (source.errorCode === SqlPreviewErrorCode.limitExceeded) {
        if (source.limit === SqlPreviewLimit.rowsPerQuery) return {kind: 'limitRowsPerQuery'}
        if (source.limit === SqlPreviewLimit.statementTimeout) return {kind: 'limitStatementTimeout'}
        if (source.limit === SqlPreviewLimit.responsePayload) return {kind: 'limitResponsePayload'}
        // WHY: unknown limit value shouldn't render "Preview unavailable"
        // (implies service down). Fall back to a generic "limit exceeded"
        // banner so the user sees the actual category.
        return {kind: 'limitGeneric'}
    }
    if (source.errorCode === SqlPreviewErrorCode.paramsInvalid) {
        return {kind: 'paramsInvalid'}
    }
    if (source.errorCode === SqlPreviewErrorCode.error && binding === SqlBindingMethod.QUERY_FOR_VALUE) {
        return {kind: 'typeConversionError', selectedType: valueType}
    }
    if (source.errorCode === SqlPreviewErrorCode.error) {
        // WHY: backend SHOULD populate message for SQL_PREVIEW_ERROR, but if it
        // doesn't, sqlError with no message resolves to a body that points the
        // user at the error log instead of leaving them with a contentless banner.
        return {kind: 'sqlError', message: source.message ?? undefined}
    }
    if (source.message) {
        return {kind: 'sqlError', message: source.message}
    }
    // WHY: unparseable error (programmer error, thrown Symbol, …) should NOT
    // imply network failure — that misleads. Use the generic sqlError fallback.
    return {kind: 'sqlError'}
}

type ErrorBannerCopy = ReturnType<typeof useI18n>['scriptSqlV2']['errorBanners']

export function resolveBannerCopy(descriptor: BannerDescriptor, copy: ErrorBannerCopy): BannerVariant {
    switch (descriptor.kind) {
        case 'networkFailure':
            return {heading: copy.networkFailure.heading, body: copy.networkFailure.body}
        case 'permissionDenied':
            return {heading: copy.permissionDenied.heading, body: copy.permissionDenied.body}
        case 'serverError':
            return {heading: copy.serverError.heading, body: copy.serverError.body}
        case 'rateLimit':
            return {
                heading: copy.rateLimit.heading,
                body: copy.rateLimit.body({retryAfterSeconds: String(descriptor.retryAfterSeconds)}),
            }
        case 'limitRowsPerQuery':
            return {heading: copy.limitRowsPerQuery.heading, body: copy.limitRowsPerQuery.body}
        case 'limitStatementTimeout':
            return {heading: copy.limitStatementTimeout.heading, body: copy.limitStatementTimeout.body}
        case 'limitResponsePayload':
            return {heading: copy.limitResponsePayload.heading, body: copy.limitResponsePayload.body}
        case 'limitGeneric':
            return {heading: copy.limitGeneric.heading, body: copy.limitGeneric.body}
        case 'paramsInvalid':
            return {heading: copy.paramsInvalid.heading, body: copy.paramsInvalid.body}
        case 'typeConversionError':
            return {
                heading: copy.typeConversionError.heading,
                body: copy.typeConversionError.body({selectedType: descriptor.selectedType}),
            }
        case 'sqlError':
            return {heading: copy.sqlError.heading, body: descriptor.message ?? copy.sqlError.bodyNoMessage}
        default: {
            // WHY (1122): exhaustiveness anchor — adding a new BannerDescriptor variant
            // without a matching case fails here with a clearer error than "not all
            // paths return a value" on the function signature alone.
            const _exhaustive: never = descriptor
            throw new Error(`Unhandled banner descriptor kind: ${JSON.stringify(_exhaustive)}`)
        }
    }
}

function extractBannerSource(err: unknown): BannerSource {
    if (err && typeof err === 'object') {
        const e = err as Record<string, unknown>
        const source: BannerSource = {}
        if (typeof e.errorCode === 'string') source.errorCode = e.errorCode
        if (typeof e.limit === 'string') source.limit = e.limit
        if (typeof e.retryAfterSeconds === 'number') source.retryAfterSeconds = e.retryAfterSeconds
        if (typeof e.httpStatus === 'number') source.httpStatus = e.httpStatus
        if (typeof e.status === 'number' && source.httpStatus === undefined) source.httpStatus = e.status
        if (typeof e.message === 'string') source.message = e.message
        if (e.isNetworkFailure === true) source.isNetworkFailure = true
        return source
    }
    return {message: String(err)}
}

// WHY: ResultTable expects {columns,rows,rowCount,truncated} (SqlExecute shape).
// The preview endpoint returns a per-binding result body — flatten it into the
// shared row/column shape so the existing ResultTable renders unchanged. Once
// the dialog stops reusing ResultTable (529.06+), this adapter can be deleted.
function adaptPreviewResponse(response: SqlPreviewResponse | null): SqlExecute | null {
    if (!response) return null
    const r = response.result
    if (r.sampleRows && r.sampleRows.length > 0) {
        // eslint-disable-next-line i18next/no-literal-string -- ResultTable wire-shape; adapter deleted in 529.06+
        const columns = Object.keys(r.sampleRows[0]).map((name) => ({name, type: 'unknown', label: name}))
        return {
            columns,
            rows: r.sampleRows as Record<string, unknown>[],
            rowCount: r.rowCount ?? r.sampleRows.length,
            truncated: r.truncated ?? false,
        }
    }
    // WHY (529.04 HQG §3): a NULL scalar IS a result, just a null one. The earlier
    // `&& r.scalarValue !== null` filter dropped legitimate `sql.queryForValue("SELECT NULL")`
    // results and the user saw "no result" instead of a single null row. Check only
    // for the field's presence, not its non-null value.
    if (r.scalarValue !== undefined) {
        return {
            // eslint-disable-next-line i18next/no-literal-string -- ResultTable wire-shape; adapter deleted in 529.06+
            columns: [{name: 'value', type: 'unknown', label: 'value'}],
            rows: [{value: r.scalarValue}],
            rowCount: 1,
            truncated: false,
        }
    }
    if (typeof r.affectedRows === 'number') {
        return {
            // eslint-disable-next-line i18next/no-literal-string -- ResultTable wire-shape; adapter deleted in 529.06+
            columns: [{name: 'affectedRows', type: 'int', label: 'Affected rows'}],
            rows: [{affectedRows: r.affectedRows}],
            rowCount: 1,
            truncated: false,
        }
    }
    if (r.ok === true) {
        return {
            // eslint-disable-next-line i18next/no-literal-string -- ResultTable wire-shape; adapter deleted in 529.06+
            columns: [{name: 'ok', type: 'bool', label: 'ok'}],
            rows: [{ok: true}],
            rowCount: 1,
            truncated: false,
        }
    }
    return {columns: [], rows: [], rowCount: r.rowCount ?? 0, truncated: r.truncated ?? false}
}
